import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import { getExperienceListOfferCount, replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ExperienceSearchTileList from '../ExperienceLists/ExperienceSearchTileList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import Places from 'constants/places'
import { arrayToObject, sortBy, take } from 'lib/array/arrayUtils'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { connect } from 'react-redux'
import { useMultiValueToggle } from 'hooks/useToggle'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'

const CarouselList = styled.div`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};
  display: grid;
  grid-row-gap: ${rem(20)};
  grid-template-areas:
    "title"
    "carousel"
    "actions";

  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "title actions"
      "carousel carousel";
    padding-bottom: ${rem(20)};
  }
`

const CarouselHeading = styled.div`
  grid-area: title;
  display: grid;
  grid-row-gap: ${rem(16)};

`

const CarouselWrapper = styled.div`
  grid-area: carousel;
`

const Actions = styled(Group)`
  grid-area: actions;
  align-self: end;
  margin-inline-start: auto;
`

const layoutContainerPadding = variables.gutterWidth / 2

const FilterChips = styled.div`
  display: flex;
  grid-row: 2;
  gap: ${rem(4)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /*
    negative margin left/right lets us 'overflow' the layout container to show more items are scrollable
    negiatve margin + padding top/bottom give pixel calculations space to not accidently hide chip borders
  */
  padding-block: ${rem(4)};
  padding-inline: ${rem(layoutContainerPadding)};
  margin-block: -${rem(4)};
  margin-inline: -${rem(layoutContainerPadding)};

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    padding: 0;
    margin: 0;
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: initial;
    }
  }
`

interface Props {
  primaryPlace: App.Place
  title: string
  parentCategoryCode?: number
  sortType?: Experiences.SortTypes
  filterMode: 'place' | 'category' | 'collection' | 'none'
  categories: Array<App.ExperienceItemCategory>
  searchFacets?: App.ExperienceListFacetsState
  collections?: Array<App.ExperienceCollectionFacet>
  trackingListName?: string
}

function ExperiencesPageSearchCarousel(props: Props) {
  const intl = useIntl()
  const { primaryPlace, parentCategoryCode = 999, sortType, title, filterMode, categories, collections, searchFacets, trackingListName } = props
  const [selectedPlace, setSelectedPlace] = useState(primaryPlace)
  const [filteredCategories, toggleCategory] = useMultiValueToggle<number>()
  const [filteredCollections, toggleCollection] = useMultiValueToggle<string>()

  const trackEvent = useTrackExperienceOfferEvent()

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const handleViewAllClick = useCallback(() => {
    trackEvent('VIEW_ALL_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  useEffect(() => {
    // our 'first place' is often our current location
    // if we changes, we should default back to that.
    // TODO: this is not an obvious solution, improve it
    if (primaryPlace !== selectedPlace) {
      setSelectedPlace(primaryPlace)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryPlace])

  const primaryCodes = useMemo(() => ([parentCategoryCode]), [parentCategoryCode])
  const primaryCollectionIds = useMemo(() => collections?.map(col => col.collectionId) ?? [], [collections])

  const primaryList = useExperienceSearchList({
    categoryCodes: primaryCodes,
    sortBy: sortType,
    placeId: primaryPlace.id,
    collections: primaryCollectionIds,
    countOnly: true,
  })

  const listCodes = useMemo(() => {
    if (filterMode === 'category' && filteredCategories.length > 0) {
      return filteredCategories
    }
    return primaryCodes
  }, [filteredCategories, primaryCodes, filterMode])

  const listCollectionIds = useMemo(() => {
    if (filterMode === 'collection' && filteredCollections.length > 0) {
      return filteredCollections
    }
    return primaryCollectionIds
  }, [filteredCollections, primaryCollectionIds, filterMode])

  const searchUrl = useMemo(() => {
    const query = replaceExperienceQueryString({
      destinationId: selectedPlace.id,
      destinationName: selectedPlace.name.split(',', 1)[0],
      categories: listCodes,
      collections: listCollectionIds,
    })
    return `/search/experiences?${query}`
  }, [selectedPlace, listCodes, listCollectionIds])

  const searchMapUrl = useMemo(() => {
    const query = replaceExperienceQueryString({
      destinationId: selectedPlace.id,
      destinationName: selectedPlace.name.split(',', 1)[0],
      categories: listCodes,
      collections: listCollectionIds,
    })
    return `/search/experiences/map?${query}`
  }, [selectedPlace, listCodes, listCollectionIds])

  const subCategories = useMemo((): Array<App.ExperienceItemCategory> => {
    const categoryFacetsById = arrayToObject(
      searchFacets?.[selectedPlace.id]?.facets?.category ?? [],
      (cat) => cat.categoryId,
    )

    // Need sub categories of the parent category we're showing and the ones that have valid results to show a list of, i.e. more than 3
    const validCategories = categories.filter(cat => cat.parentId === parentCategoryCode && (categoryFacetsById[cat.id]?.count ?? 0) >= 4)
    const sortedCategoriesByCount = sortBy(validCategories, cat => categoryFacetsById[cat.id] ?? 0, 'desc')
    return take(sortedCategoriesByCount, 3)
  }, [searchFacets, selectedPlace.id, categories, parentCategoryCode])

  // We don't want users to see the same set of experiences when they swap to a different place
  // ensure this happens by excluding the main place from the results of other places
  const placeIdToIgnore = selectedPlace.id !== primaryPlace.id ? primaryPlace.id : undefined

  const analyticsListName = useMemo(() => {
    if (trackingListName) {
      return trackingListName
    }
    return getListName('OfferList', 'EXPERIENCES', `Experiences ${title} Carousel`)
  }, [trackingListName, title])

  const tracking = useOfferListTracking(
    analyticsListName,
    { key: primaryPlace.id },
  )

  // we want to show a minimum of one full row on the experiences page, which is 4
  if (getExperienceListOfferCount(primaryList) < 4) {
    return null
  }

  const aroundTheWorldLabel = intl.formatMessage({
    id: '-L7aT6yWy5RWvnvQay7Q',
    defaultMessage: 'Around the world',
    description: 'Filter chip label for worldwide experiences on experiences landing page search carousel',
  })

  const canChangeLocation = filterMode === 'place' && primaryPlace.id !== Places.Anywhere.id
  const canChangeCategories = filterMode === 'category' && subCategories.length > 1
  const canChangeCollections = filterMode === 'collection' && collections && collections.length > 1
  const hasFilters = canChangeLocation || canChangeCategories || canChangeCollections

  return (
    <CarouselList>
      <CarouselHeading>
        <Heading variant="heading3">{title}</Heading>
        {hasFilters && <FilterChips>
          {canChangeLocation && [primaryPlace, Places.Anywhere].map(place => <FilterChip
            key={place.id}
            size="medium"
            selected={selectedPlace.id === place.id}
            onClick={() => setSelectedPlace(place)}
          >
            {place === Places.Anywhere ? aroundTheWorldLabel : place.name}
          </FilterChip>,
          )}
          {canChangeCategories && subCategories.map(cat => <FilterChip
            key={cat.id}
            size="medium"
            selected={filteredCategories.includes(cat.id)}
            onClick={() => toggleCategory(cat.id)}
          >
            {cat.name}
          </FilterChip>,
          )}
          {canChangeCollections && collections?.map(col => <FilterChip
            key={col.collectionId}
            size="medium"
            selected={filteredCollections.includes(col.collectionId)}
            onClick={() => toggleCollection(col.collectionId)}
          >
            {col.theme}
          </FilterChip>,
          )}
        </FilterChips>}
      </CarouselHeading>
      <CarouselWrapper>
        <OfferListEventsProvider tracking={tracking}>
          <ExperienceSearchTileList
            placeId={selectedPlace.id}
            sortBy={sortType}
            categories={listCodes}
            placeIdToIgnore={placeIdToIgnore}
            collections={listCollectionIds}
          />
        </OfferListEventsProvider>
      </CarouselWrapper>
      <Actions direction="horizontal" gap={4}>
        <TextButton
          kind="tertiary"
          to={searchMapUrl}
          onClick={handleViewMapClick}
          startIcon={<LineMapIcon />}
        >
          {intl.formatMessage({
            id: 'G8wU7GcMb077RMVwgqHb',
            defaultMessage: 'View on map',
            description: 'Button text to view experiences on a map in the experiences search carousel',
          })}
        </TextButton>
        <TextButton
          kind="tertiary"
          to={searchUrl}
          onClick={handleViewAllClick}
        >
          {intl.formatMessage({
            id: '54doUoHmthKSVntUhdMF',
            defaultMessage: 'View all',
            description: 'Button text to view all experiences in the experiences search carousel',
          })}
        </TextButton>
      </Actions>
    </CarouselList>
  )
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
    searchFacets: state.experience.experienceListFacets,
  }
}

export default connect(mapStateToProps)(ExperiencesPageSearchCarousel)
